<template>
  <Teleport defer to="#shop-the-look">
    <div class="page-width">
      <h3 class="t4">
        {{ terms.title }}
      </h3>
      <ul v-if="hasItems" class="ShopTheLook__Items">
        <li v-for="product in products" :key="product.variant" class="ShopTheLook__Item">
          <component :is="conditionalElement(product.handle)" v-bind="conditionalProps(product)" :class="onViewClass(product)">
            <div class="ShopTheLook__ImageWrap" :class="loadedImageClass(product)">
              <img loading="lazy" :src="product.image.url" :alt="product.image.altText" @load="handleMedia(product)" />
            </div>
            <div class="ShopTheLook__Details">
              <h4 class="t3">{{ product.title }}</h4>
              <p class="p5">{{ conditionalCopy(product) }}</p>
            </div>
          </component>
        </li>
      </ul>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useProductStore } from './Composables/Store';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { formatID } from '@utils/format';
import { PRODUCT_STL_QUERY } from './Composables/Queries';

const store = useProductStore();

const props = defineProps({
  terms: Object,
});

const items = store.$state.shopTheLook ?? [];
const products = ref([]);

/**
 * Generates headers for the GraphQL query
 * @param {string} handle - Product handle
 * @returns {Promise<Object>} GraphQL query headers
 */
const headers = (handle) => {
  return {
    variables: { handle },
    query: PRODUCT_STL_QUERY,
  };
};

/**
 * Computes the CSS class for loaded images
 * @returns {Function} Function returning class name based on product load state
 */
const loadedImageClass = computed(() => {
  return (product) => (product.loaded ? 'ShopTheLook__ImageWrap--Loaded' : '');
});

/**
 * Computes the CSS class for active products
 * @returns {Function} Function returning class name based on product visibility
 */
const onViewClass = computed(() => {
  return (product) => {
    return matchesCurrentURL(product.handle) ? 'ShopTheLook__ItemWrap ShopTheLook__ItemWrap--Active' : 'ShopTheLook__ItemWrap';
  };
});

/**
 * Checks if there are items to display
 * @returns {boolean}
 */
const hasItems = computed(() => products.value.length > 0);

/**
 * Marks product media as loaded and transitions the image in
 * @param {Object} product - The product object
 */
const handleMedia = (product) => (product.loaded = true);

/**
 * Adds active class to the container and parent elements to transition in
 */
const handleVisibility = () => {
  const container = document.getElementById('shop-the-look');
  const parent = container.parentElement;
  parent.classList.add('ShopTheLook-Container--Active');
  container.classList.add('ShopTheLook--Active');
};

/**
 * Checks if the product handle matches the current URL
 * @param {string} handle - The product handle
 * @returns {boolean}
 */
const matchesCurrentURL = (handle) => {
  const value = handle.replace('-seasonal', '').trim();
  const current = window.location.pathname.replace('-seasonal', '').trim();
  return current.includes(value);
};

/**
 * Determines whether the component should be a <div> or <a>
 * @param {string} handle - The product handle
 * @returns {string} - 'div' or 'a'
 */
const conditionalElement = (handle) => {
  return matchesCurrentURL(handle) ? 'div' : 'a';
};

/**
 * Extracts terms from props
 * @returns {Object} The terms object
 */
const terms = computed(() => props.terms.shop_the_look);

/**
 * Generates conditional properties for elements
 * @param {Object} item - The product item
 * @returns {Object} - Empty Object or Object with href property if applicable
 */
const conditionalProps = (item) => {
  if (matchesCurrentURL(item.handle)) return {};

  const url = new URL(window.location);
  const params = url.searchParams;

  if (params.has('variant')) {
    params.set('variant', item.variant);
  }

  url.pathname = `/products/${item.handle.replace('-seasonal', '').trim()}`;
  return { href: url.href };
};

/**
 * Computes CTA text
 * @returns {String} String containing 'Shop Now' or 'Currently Viewing'
 */
const conditionalCopy = computed(() => {
  return (product) => (matchesCurrentURL(product.handle) ? terms.value.onview : terms.value.shop);
});

/**
 * Fetches product data and updates the local state
 */
onMounted(async () => {
  const data = await Promise.all(
    items.map(async (item) => {
      const request = headers(item.handle);
      const response = await fetchStorefront(request).then((res) => clean(res.data));

      if (!response?.product) return null;

      const { variants, title } = response.product;
      const firstAvailableVariant = variants.find((variant) => variant.quantityAvailable > 0);
      const variant = variants.find((variant) => formatID(variant.id) === Number(item.variant)) || firstAvailableVariant;

      if (variant) {
        return {
          title,
          loaded: false,
          handle: item.handle,
          image: variant.image,
          variant: formatID(variant.id),
        };
      }
      return null;
    })
  );

  products.value = data.filter((item) => item !== null);
  handleVisibility();
});
</script>
